import { FC, ReactNode } from "react"
import { getTopMenuFlyoutLayersPlugins } from "plugins/plugin-registry"

export type TopMenuFlyoutEntityData = {
    id: string
    plugin: ReturnType<typeof getTopMenuFlyoutLayersPlugins>[number]
}

type TopMenuFlyoutGroupAccumulator = {
    flyoutId: string
    menuConfig: {
        TriggerComponent: FC<{
            entityCount: number
            open: boolean
        }>
        MenuComponent: FC<{
            children: ReactNode
        }>
    } | null
    entitiesData: TopMenuFlyoutEntityData[]
}

export type TopMenuFlyoutGroup = {
    flyoutId: string
    menuConfig: {
        TriggerComponent: FC<{
            entityCount: number
            open: boolean
        }>
        MenuComponent: FC<{
            children: ReactNode
        }>
    }
    entitiesData: TopMenuFlyoutEntityData[]
}

export const useTopMenuFlyoutLayerGroups = (): TopMenuFlyoutGroup[] => {
    const plugins = getTopMenuFlyoutLayersPlugins()
    const data: Record<string, TopMenuFlyoutGroupAccumulator> = plugins.reduce(
        (acc, curPlugin) => {
            if (!acc[curPlugin.flyoutId]) {
                acc[curPlugin.flyoutId] = {
                    flyoutId: curPlugin.flyoutId,
                    menuConfig: null,
                    entitiesData: [],
                }
            }
            if (curPlugin.menuConfig) {
                if (acc[curPlugin.flyoutId].menuConfig) {
                    throw new Error(
                        `Two or more top-menu-flyout-layers-plugin's registered under flyoutId: "${curPlugin.flyoutId}", both defining a menuConfig.`
                    )
                }
                acc[curPlugin.flyoutId].menuConfig = curPlugin.menuConfig
            }
            const entities = curPlugin.useEntities()
            entities.forEach((entity) => {
                acc[curPlugin.flyoutId].entitiesData.push({ id: entity.id, plugin: curPlugin })
            })
            return acc
        },
        {} as Record<string, TopMenuFlyoutGroupAccumulator>
    )
    return Object.values(data)
        .filter((group) => group.menuConfig)
        .map(({ flyoutId, menuConfig, entitiesData }) => ({
            flyoutId,
            menuConfig: menuConfig!,
            entitiesData,
        }))
}
